import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { Trash2Icon } from '@/shared/ui/icons';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiMePatch, useCurrentUser } from '@/entities/auth';
import { useDeleteAccount } from '@/features/auth';
import { FeaturesProfileAvatarPicker, FeaturesProfileProfileForm } from '@/features/profile';
import { DebtsCard } from './DebtsCard';
import { ReceiptsCard } from './ReceiptsCard';
import {
  emptyProfileFormValues,
  mapFormValuesToPayload,
  mapProfileToFormValues,
  profileFormSchema,
  type ProfileFormValues,
} from '@/features/profile/modules/types';
import type { ApiAuthMePatchBody } from '@/shared/types/api/auth';

const FORM_ID = 'profile-form';
const BACK_TO = '/settings';

export const WidgetsProfileMain = () => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const toast = useToast();
  const profile = useCurrentUser();
  const deleteAccount = useDeleteAccount();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: emptyProfileFormValues,
    mode: 'onBlur',
  });

  const data = profile.data;
  useEffect(() => {
    if (data) form.reset(mapProfileToFormValues(data));
    // reset once per loaded profile; avatar refetches must not clobber in-progress edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const mutation = useMutate({
    api: (payload: ApiAuthMePatchBody) => apiMePatch(payload),
    invalidateKeys: [queryKeys.auth.me],
    onSuccess: () => toast.success(t('toast.saved')),
    onError: (_err, message) => toast.error(message),
  });

  const onSubmit = (values: ProfileFormValues): void => {
    mutation.mutate(mapFormValuesToPayload(values));
  };

  return (
    <>
      <PageHeader title={t('title')} showBack backTo={BACK_TO} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {profile.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : profile.isError || !data ? (
          <ErrorState
            message={profile.error ? extractErrorMessage(profile.error) : t('loadError')}
            onRetry={() => void profile.refetch()}
          />
        ) : (
          <>
            <FeaturesProfileAvatarPicker avatarUrl={data.avatar_url} />
            <FeaturesProfileProfileForm
              id={FORM_ID}
              form={form}
              email={data.email}
              onSubmit={onSubmit}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(BACK_TO)}
                disabled={mutation.isPending}
                block
              >
                {t('common:cancel')}
              </Button>
              <Button type="submit" form={FORM_ID} loading={mutation.isPending} block>
                {t('common:save')}
              </Button>
            </div>

            <DebtsCard />
            <ReceiptsCard />

            <div className="mt-2 border-t border-slate-100 pt-4">
              <Button
                type="button"
                variant="danger"
                leftIcon={<Trash2Icon className="h-4 w-4" aria-hidden />}
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deleteAccount.isPending}
                block
              >
                {t('deleteAccount.button')}
              </Button>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t('deleteAccount.modal.title')}
        description={t('deleteAccount.modal.description')}
        confirmLabel={t('deleteAccount.modal.confirm')}
        cancelLabel={t('deleteAccount.modal.cancel')}
        confirmVariant="danger"
        loading={deleteAccount.isPending}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={() => deleteAccount.mutate()}
      />
    </>
  );
};
